import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAppContext } from 'services/context';
import { useScreenSize, useToggle, useTranslation } from 'services/hooks';
import { COURSES_ACTIONS } from 'services/reducers/actionTypes';
import { COURSE_STATUS, ROUTES, MEETING_STATUS, COURSE_TYPES } from 'constants/index.js';
import { saveArrayAsCSV } from 'services/utils';
import Button from 'components/Button';
import Container from 'components/Container';
import Icon from 'components/Icon';
import Input from 'components/Input';
import Modal from 'components/Modal';
import Spinner from 'components/Spinner';
import Tooltip from 'components/Tooltip';
import UnsupportedFile from 'components/UnsupportedFile';
import classNames from 'classnames';
import Box from 'components/Box';
import './style.css';

const MOBILE_SIZE = process.env.REACT_APP_MOBILE_SIZE;
const API_URL = process.env.REACT_APP_API_URL;
const TRANSLATION_KEY = 'my_course';

const MyCourse = () => {
	const { Api, courses, user, dispatchCourses, isLoading } = useAppContext();
	const navigate = useNavigate();
	const getTranslation = useTranslation();

	const [pdfBlob, setPdfBlob] = useState(undefined);
	const [certificatePdf, setCertificatePdf] = useState(undefined);
	const [pdfToDisplay, setPdfToDisplay] = useState(undefined);

	const [studentDetails, setStudentDetails] = useState(user);

	const [studentMeetings, setStudentMeetings] = useState(undefined);

	const [dynamicParams, setDynamicParams] = useState([]);

	const [userDetailsFormModal, setUserDetailsFormModalToggle] = useToggle(false);
	const [popupBeforeCertificateModal, setPopupBeforeCertificateModalToggle] = useToggle(false);
	const [certificateModal, setCertificateModalToggle] = useToggle(false);
	const [pdfModal, PdfModalToggle] = useToggle(false);
	const [showAccessCode, setShowAccessCode] = useState(false);
	const [reviewExist, setReviewExist] = useState(false);
	const [formValidationError, setFormValidationError] = useState(false);

	const toolTipRef = useRef();
	const studentScheduleContainer = useRef();
	const { recordId } = useParams();
	const screenSize = useScreenSize();

	const currentCourse = useMemo(() => {
		const _currentCourse = courses.currentCourse;
		return _currentCourse;
	}, [courses]);

	const backgroundImageByCourse = useMemo(() => {
		switch (currentCourse.courseTypeId) {
			case COURSE_TYPES.ESL:
				return 'ESL';
			case COURSE_TYPES.IELTS:
				return 'IELTS';
		}
	}, [currentCourse]);

	//returns class as string for dynamic class css
	const classesByRateOfProgress = useMemo(() => {
		const rateOfProgress = parseInt(currentCourse?.myCourseRateOfProgress) || 0;
		const formSent = currentCourse.isSentMyCourseForm;
		const _hasMeetings = currentCourse.hasMeetings;

		if ((currentCourse.studentClassId || _hasMeetings) && rateOfProgress >= 25 && rateOfProgress < 100) {
			return COURSE_STATUS.IN_PROGRESS;
		}
		if ((!currentCourse.studentClassId || !_hasMeetings) && formSent) {
			return COURSE_STATUS.NEW_FORM_SENT;
		}
		if (rateOfProgress >= 100) {
			return COURSE_STATUS.COMPLETED;
		}
		return COURSE_STATUS.NEW;
	}, [currentCourse]);

	//fetch parameters for administrationContact
	useEffect(() => {
		async function fetchDynamicParams() {
			const _dynamicParams = await Api.GetDynamicParams();
			setDynamicParams(_dynamicParams);
		}
		fetchDynamicParams();
	}, []);

	//navigate to course review page - for btn on completed screen
	const redirectToReview = recordId => {
		if (!recordId) return;
		navigate(`${ROUTES.REVIEW}/${recordId}`);
	};

	//navigate on new tab to whatsapp
	const redirectToWhatsApp = (e, number) => {
		window.open(`https://wa.me/${number.content}`, '_blank');
	};

	//export student schedule to excel
	const _handleExportToExcel = () => {
		const meeting = [...studentMeetings];
		const courseName = currentCourse.courseName;
		const meetingsForExcel = meeting.map((meet, index) => {
			return {
				Session: `Session ${index + 1}`,
				Date: meet.date,
				Start_Time: meet.startTime,
				End_Time: meet.endTime,
				Teacher: meet.teacherName,
				Zoom_Link: meet.meetingRemark,
			};
		});

		saveArrayAsCSV(`Galway_${courseName}_schedule_` + new Date().toLocaleDateString('en-GB'), meetingsForExcel);
	};

	const generatePDF = () => {
		if (studentScheduleContainer) {
			// Use html2canvas to capture the element as an image

			html2canvas(studentScheduleContainer.current).then(canvas => {
				const imgData = canvas.toDataURL('image/png');
				const pdf = new jsPDF({ orientation: 'landscape' });

				const pdfWidth = pdf.internal.pageSize.getWidth();
				const pdfHeight = pdf.internal.pageSize.getHeight();

				const padding = 20; // You can increase this to add more space around the content

				const availableWidth = pdfWidth - 2 * padding;
				const availableHeight = pdfHeight - 2 * padding;

				const canvasWidth = canvas.width;
				const canvasHeight = canvas.height;

				const widthRatio = availableWidth / canvasWidth;
				const heightRatio = availableHeight / canvasHeight;
				const scale = Math.min(widthRatio, heightRatio);

				const scaledWidth = canvasWidth * scale;
				const scaledHeight = canvasHeight * scale;

				const xPosition = (pdfWidth - scaledWidth) / 2;
				const yPosition = (pdfHeight - scaledHeight) / 2;

				pdf.addImage(imgData, 'PNG', xPosition, yPosition, scaledWidth, scaledHeight);

				const pdfName = `${user.fname}-${user.lname}_${currentCourse.courseName}_Schedule`;
				pdf.save(pdfName);
			});
		} else {
			console.error('Element not found');
		}
	};

	//handles revel/generate activationCode
	const _handleRevealActivationCode = async (e, item) => {
		e?.preventDefault();
		if (showAccessCode) return;
		let _currentCourse = courses.courses.find(course => course.recordId === parseInt(recordId));
		const _studentCourseActivationCode = _currentCourse?.studentCourseActivationCode || '';
		const isDebug = API_URL.includes('.test') || API_URL.includes('localhost');

		if (isDebug) {
			currentCourse.studentCourseActivationCode = 'TEST-TEST-TEST-TEST-TEST-TEST';
			setShowAccessCode(true);
			return;
		}

		if (!_studentCourseActivationCode) {
			_currentCourse = await Api.GetActivationCode(recordId);
			const userCourses = await Api.GetUserCourses();
			if (userCourses?.length > 0) {
				dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
				const releventCourse = userCourses.find(course => course.recordId === parseInt(recordId));
				dispatchCourses({ type: COURSES_ACTIONS.SET_CURRENT_COURSE, payload: releventCourse });
			}
		}

		setShowAccessCode(true);
	};

	//handles student details form trigger when user didnt sent the form already
	const _handleOpenUserDetailsModal = () => {
		if (!currentCourse.isSentMyCourseForm) {
			setUserDetailsFormModalToggle();
			return;
		}
	};

	//handle open certificate popup
	const _handleGetCertificateBtn = e => {
		e?.preventDefault();
		if (reviewExist) {
			_handleCertificate();
			return;
		}
		setPopupBeforeCertificateModalToggle();
	};

	//handles redirect to Conversation
	const _handleContactYourTeacher = () => {
		navigate(ROUTES.CONVERSATION, {
			state: { contactTeacher: true, studentTeacherId: currentCourse.studentTeacherId, recordId: currentCourse.recordId },
		});
	};

	const _handleCourseMaterialsNavigate = () => {
		navigate(`/${ROUTES.COURSE}/${recordId}`);
	};

	//copy to clipboard
	const _copyToClipboard = (value, e) => {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(value);
			if (e) toolTipRef.current.Show({ x: e.pageX || -10, y: e.pageY || -10 });
		}
	};

	//contains all courseMaterials left box
	const courseMaterials = useMemo(() => {
		return [
			{
				id: 1,
				group: 'courseMaterials',
				name: 'ActivationCode',
				title: '',
				iconName: 'eyeIcon',
				copyToClipboard: showAccessCode && (
					<Icon
						icon="copyClipboardIcon"
						size={20}
						onClick={e => {
							e?.preventDefault();
							_copyToClipboard(currentCourse?.studentCourseActivationCode, e);
						}}
					/>
				),
				onClick: _handleRevealActivationCode,
				renderAccessCode: showAccessCode ? (
					<>
						<div>{currentCourse?.studentCourseActivationCode}</div>
					</>
				) : (
					'Show access code'
				),
			},
			{
				id: 2,
				group: 'courseMaterials',
				name: 'educationPlatform',
				title: 'Continue to education platform',
				iconName: 'rightArrow',
				onClick: e => {
					e?.preventDefault();
					if (!currentCourse) return;
					window.open(currentCourse.courseExternalURL, '_blank');
				},
			},
			{
				id: 3,
				group: 'courseMaterials',
				name: 'onlineMaterials',
				title: 'How to access your online materials',
				iconName: 'downloadIconV2',
				pdfUrl: currentCourse?.guideFileUrl || '',
				iconColor: '#d41028',
				onClick: (e, pdf) => {
					_handleViewPdf(pdf.pdfUrl);
				},
			},
			{
				id: 4,
				group: 'courseMaterials',
				name: 'courseGuide',
				title: 'Galway Academy ESL course guide',
				iconName: 'downloadIconV2',

				pdfUrl: currentCourse?.introductionFileURL || '',

				onClick: (e, pdf) => {
					_handleViewPdf(`${API_URL}/${pdf.pdfUrl}`);
				},
			},
		];
	}, [currentCourse, showAccessCode]);

	//contains all administationContact right box
	const administationContact = [
		{
			id: 1,
			group: 'administationContact',
			name: 'whatsApp',
			title: 'Contact us by WhatsApp',
			iconName: 'whatsAppIcon',
			iconColor: '#8D8D8D',
			content: '447723528081',
			onClick: redirectToWhatsApp,
		},
		{
			id: 2,
			group: 'administationContact',
			name: 'email',
			title: 'Send us an Email',
			iconColor: '#8D8D8D',
			iconName: 'emailIcon',
			onClick: e => {
				e?.preventDefault();
				const supportEmail = dynamicParams.find(param => param.paramName === 'CsrEmail');
				if (!supportEmail) return;

				window.location = `mailto:${supportEmail.paramValue}`;
			},
		},
		{
			id: 3,
			group: 'administationContact',
			name: 'phone',
			iconColor: '#8D8D8D',
			number: '35315692414',
			title: `Call us to +353-1569-24-14`,
			iconName: 'phoneIcon',
			onClick: e => {
				e?.preventDefault();
				const SitePhone = dynamicParams.find(param => param.paramName === 'SitePhone');
				if (!SitePhone) return;

				window.location = `tel:${SitePhone.paramValue}`;
			},
		},
	];

	const myCourseClassName = classNames('MyCourse', backgroundImageByCourse, {
		[classesByRateOfProgress]: classesByRateOfProgress === COURSE_STATUS.COMPLETED,
	});

	//clear certificate states
	useEffect(() => {
		return () => {
			setCertificatePdf();
			setPdfBlob();
		};
	}, []);

	//generate certificate
	const _handleCertificate = async () => {
		if (!recordId) return;
		if (pdfBlob && certificatePdf) {
			return;
		}

		const _pdfBlob = await Api.GetCertificate(parseInt(recordId));
		const _pngBlob = await Api.GetCertificatePng(parseInt(recordId));
		if (!_pdfBlob || !_pngBlob) return;

		const reader = new FileReader();
		reader.onloadend = function () {
			const base64data = reader.result;
			setCertificatePdf(base64data);
		};

		reader.readAsDataURL(_pngBlob);
		setPdfBlob(_pdfBlob);
		setCertificateModalToggle();
		if (popupBeforeCertificateModal) setPopupBeforeCertificateModalToggle();
	};

	//handle show pdf on pop up
	const _handleViewPdf = url => {
		if (!url) return;
		setPdfToDisplay(url);
		PdfModalToggle();
	};

	//handle pdf download on certificate popup
	const _handleDownLoadPdf = () => {
		if (!pdfBlob) return;

		const courseName = currentCourse?.courseName || '';
		const link = document.createElement('a');
		link.href = URL.createObjectURL(pdfBlob); //create a blobURI pointing to our Blob
		link.download = `${courseName} Certificate`; // some browser needs the anchor to be in the doc
		link.click();
		setTimeout(() => URL.revokeObjectURL(link.href), 1000); // in case the Blob uses a lot of memory
	};

	//handles changing inputs
	const _handleChangeInput = (value, key) => {
		const _userDetails = { ...studentDetails };
		_userDetails[key] = value;
		setStudentDetails(_userDetails);
	};

	//handles onClick send user details form
	const _handleUserDetailsFormSend = async e => {
		e?.preventDefault();
		setFormValidationError(false);
		const form = new FormData(e.target);
		let formData = Object.fromEntries(form.entries());
		formData.recordId = currentCourse.recordId;
		for (const key in formData) {
			if (!formData[key]) {
				setFormValidationError(true);
				return;
			}
		}
		formData.firstName = user.fname;
		formData.lastName = user.lname;
		formData.email = user.email;
		formData.whatsApp = Boolean(parseInt(formData.whatsApp));

		const res = await Api.SaveMyCourseForm(formData);
		if (!res) return;
		const userCourses = await Api.GetUserCourses();
		if (!userCourses) return;
		const releventCourse = userCourses.find(course => course.recordId === parseInt(recordId));
		dispatchCourses({ type: COURSES_ACTIONS.SET_CURRENT_COURSE, payload: releventCourse });
		dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
		setUserDetailsFormModalToggle();
	};

	//return relevent JSX element on inProgress status - 1-with meetings, 2-without meetings
	const RenderInProgress = () => {
		if (!studentMeetings) return null;
		if (studentMeetings?.length > 0) {
			return (
				<>
					<div className="InProgress__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_in_progress_title`)?.replace('[name]', user.fname)}
					</div>
					<div className="InProgress__sub_title" onClick={generatePDF}>
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_in_progress_sub_title`)}{' '}
						<Icon icon="downloadIconV2" color="black" size={20} />
					</div>
					<div className="InProgress__student_schedule" ref={studentScheduleContainer}>
						{(currentCourse.courseTypeId === COURSE_TYPES.IELTS || screenSize.width >= MOBILE_SIZE) && (
							<StudentSchedule
								extraClassName={backgroundImageByCourse}
								meetings={studentMeetings}
								courseType={currentCourse.courseTypeId}
							/>
						)}

						{currentCourse.courseTypeId !== COURSE_TYPES.IELTS && screenSize.width < MOBILE_SIZE && (
							<StudentScheduleMobile
								extraClassName={backgroundImageByCourse}
								meetings={studentMeetings}
								courseType={currentCourse.courseTypeId}
							/>
						)}
					</div>
				</>
			);
		}
		return (
			<>
				<div className="InProgress__title">
					{
						getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_in_progress_no_meeting_title`)?.replace[
							('[course]', currentCourse.courseName)
						]
					}
				</div>
				<div
					className="no_meeting"
					dangerouslySetInnerHTML={{
						__html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_in_progress_no_meeting_sub_title`)?.replace(
							'[course]',
							currentCourse.courseName
						),
					}}
				></div>
			</>
		);
	};

	//returns JSX by user rate of progress (NEW/NEW_FORM_SENT/INPROGRESS/COMPLETED)
	const renderByStatus = useMemo(() => {
		//New after user sends user details form
		if (classesByRateOfProgress === COURSE_STATUS.NEW_FORM_SENT) {
			return (
				<>
					<div className="New_form-sended__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_form_title`)
							?.replace('[name]', user.fname)
							?.replace('[course]', currentCourse.courseName)}
					</div>
					<div
						className="New_form-sended__sub_title"
						dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_form_sub_title`) }}
					></div>
					<div className="New_form-sended__notice">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_form_notice`)}</div>
				</>
			);
		}

		//In Progress
		if (classesByRateOfProgress === COURSE_STATUS.IN_PROGRESS) {
			return (
				<>
					<RenderInProgress />
					{studentMeetings?.length <= 0 && <Spinner visible={isLoading} />}
					{(currentCourse?.studentTeacherId && (
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_in_progress_button`)}
							startIcon="ContactIconV2"
							iconSize={20}
							extraClassName="contact_teacher"
							onClick={_handleContactYourTeacher}
						/>
					)) ||
						null}
				</>
			);
		}

		//Completed
		if (classesByRateOfProgress === COURSE_STATUS.COMPLETED) {
			return (
				<>
					<div className="Completed__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_title`)?.replace('[course]', currentCourse.courseName)}
					</div>
					<div className="Completed__sub_title">
						<span dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_sub_title`) }}></span>{' '}
						<a href="https://galwayacademy.com/courses" target="_blank">
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_link`)}
						</a>
						.
					</div>
					<div className="Completed__btn_wrapper">
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_certificate_button`)}
							startIcon="certificateIcon"
							iconSize={22}
							small
							onClick={_handleGetCertificateBtn}
						/>
						{!reviewExist && (
							<Button
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_completed_review_button`)}
								small
								startIcon="reviewIcon"
								iconSize={22}
								onClick={() => redirectToReview(currentCourse.recordId)}
							/>
						)}
					</div>
				</>
			);
		}

		//New
		return (
			<>
				<div className="New__top">
					<div className="New__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_title`)
							?.replace('[name]', user.fname)
							?.replace('[course]', currentCourse.courseName)}
					</div>
					<div
						className="New__sub_title"
						dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_sub_title`) }}
					></div>
					<Button title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_button`)} small onClick={_handleOpenUserDetailsModal} />
					<div className="New__top__notice">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_new_notice`)}</div>
				</div>
			</>
		);
	}, [currentCourse, classesByRateOfProgress, studentMeetings, screenSize, reviewExist, getTranslation]);

	//fetch user and teacher meetings when the user is InProgress and have meetings
	useEffect(() => {
		async function fetchStudentMeetingsByCourseType(studentId, recordId, classId, teacherId = null) {
			if (!studentId || !recordId) return;
			let meetings;
			let meetingObj = {};
			let meetingArray = [];
			switch (currentCourse.courseTypeId) {
				case COURSE_TYPES.ESL:
					meetings = await Api.GetStudentAndTeacherMeetings(studentId, teacherId, recordId);
					if (meetings && meetings.studentAndTeacher.length > 0) {
						meetings = meetings?.studentAndTeacher.filter(meet => meet.meetingStatusId !== MEETING_STATUS.Not_Arrived);

						meetings.map(({ meetingId, date, startTime, endTime, teacherName, meetingRemark }) => {
							meetingObj = {
								meetingId,
								date,
								startTime,
								endTime,
								teacherName,
								meetingRemark,
							};
							meetingArray.push(meetingObj);
						});
					}
					break;
				case COURSE_TYPES.IELTS:
					meetings = await await Api.GetAllCoursesMeetings(classId);
					if (meetings && meetings.upcomings.length > 0) {
						meetings = meetings?.upcomings.filter(meet => meet.meetingStatusId !== MEETING_STATUS.Not_Arrived);
						meetings.map(({ meetingId, date, startTime, endTime, teacherName, meetingRemark }) => {
							meetingObj = {
								meetingId,
								date,
								startTime,
								endTime,
								teacherName,
								meetingRemark,
							};
							meetingArray.push(meetingObj);
						});
					}
			}

			setStudentMeetings(meetingArray);
		}

		async function fetchExistReview(recordId) {
			if (!recordId) return;
			const questions = await Api.GetReviewQuestions(recordId);
			if (!questions) return;
			setReviewExist(questions?.isQuestionnaireExists || false);
		}
		if (classesByRateOfProgress === COURSE_STATUS.IN_PROGRESS && currentCourse.hasMeetings) {
			fetchStudentMeetingsByCourseType(studentDetails.userId, currentCourse.recordId, currentCourse.studentClassId);
		}
		//check if review already filled
		if (classesByRateOfProgress === COURSE_STATUS.COMPLETED) {
			fetchExistReview(parseInt(recordId));
		}
	}, [currentCourse, classesByRateOfProgress, studentDetails]);

	return (
		<div className={myCourseClassName}>
			<Container>
				<Button
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_back_button`)}
					type="goBack"
					startIcon="leftArrow"
					small
					onClick={e => {
						e?.preventDefault();
						navigate(ROUTES.PERSONAL_AREA);
					}}
				/>
				<div className={`MyCourse__top ${classesByRateOfProgress}`}>
					{renderByStatus}

					<div className="MyCourse__top__buttons">
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_materials_button`)}
							small
							extraClassName="materials"
							onClick={_handleCourseMaterialsNavigate}
						/>
						{classesByRateOfProgress !== COURSE_STATUS.COMPLETED && (
							<Button
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_teacher_button`)}
								small
								extraClassName="teacher"
								onClick={() => {
									window.open(currentCourse.introductionFileURL, '_blank');
								}}
							/>
						)}
					</div>
					<div className="MyCourse__top__guide">
						<div className="MyCourse__top__guide__title">
							{`${currentCourse.courseName} ${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_guide_title`)}`}
						</div>
						<a className="MyCourse__top__guide__link" target="_blank" href={currentCourse.guideFileUrl}>
							{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_guide_link`)}
						</a>
					</div>
				</div>

				<div className="MyCourse__grid">
					{/* <div className="MyCourse__course_materials">
						<Box
							headTitle="Course materials"
							content={courseMaterials}
							iconSize={23}
							onClick={courseMaterials.onClick}
							renderItem={(item, { iconSize, leftIcons }) => {
								const { title, id, name, iconName, iconColor, group, onClick, renderAccessCode, copyToClipboard } = item;

								const onClickCheck = e => onClick && typeof onClick === 'function' && onClick(e, item);
								return (
									<div className={`item_wrapper ${group}__${name}`} key={id}>
										<div className={`item_wrapper__title`} onClick={e => onClickCheck(e, item)}>
											{renderAccessCode}
											{copyToClipboard}
											{leftIcons && <Icon icon={iconName} color={iconColor} size={iconSize} onClick={e => onClickCheck(e, item)} />}
											{title}
											{!copyToClipboard && !leftIcons && <Icon icon={iconName} color={iconColor} size={iconSize} />}
										</div>
									</div>
								);
							}}
						/>
					</div> */}
					<div className="MyCourse__administationContact">
						<Box
							headTitle="Administration"
							content={administationContact}
							leftIcons
							iconSize={22}
							onClick={administationContact.onClick}
							renderItem={(item, { iconSize, leftIcons }) => {
								const { title, id, name, iconName, iconColor, group, onClick, number } = item;
								return (
									<div className={`item_wrapper ${group}`} key={id}>
										<div
											className={`item_wrapper__title ${name}`}
											onClick={e => onClick && typeof onClick === 'function' && onClick(e, item)}
										>
											{leftIcons && <Icon icon={iconName} size={iconSize} color={iconColor} />}

											{name === 'phone' ? <a href={`tel:${number}`}>{title}</a> : title}
											{!leftIcons && <Icon icon={iconName} size={iconSize} color={iconColor} />}
										</div>
									</div>
								);
							}}
						/>
					</div>
				</div>

				<Modal extraClassName="confirm_user_details" isVisible={userDetailsFormModal} setVisibility={setUserDetailsFormModalToggle}>
					<div className="confirm_user_details__title">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_title`)}
					</div>
					<form onSubmit={_handleUserDetailsFormSend}>
						<div className="confirm_user_details__form_wrapper">
							{/* <Input
								label="First Name"
								defaultValue={studentDetails?.fname}
								endIcon="editIcon"
								colorIcon="#7A7A7A"
								name="firstName"
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'firstName')}
							/>
							<Input
								label="Last Name"
								defaultValue={studentDetails?.lname}
								endIcon="editIcon"
								colorIcon="#7A7A7A"
								name="lastName"
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'lastName')}
							/>
							<Input
								label="Email"
								defaultValue={studentDetails?.email}
								endIcon="editIcon"
								colorIcon="#7A7A7A"
								name="email"
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'email')}
							/> */}
							<Input
								label={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_phone_field`)}
								defaultValue={studentDetails?.mainPhoneNumber}
								type="number"
								endIcon="editIcon"
								colorIcon="#7A7A7A"
								name="phone"
								onChange={({ target }) => _handleChangeInput(target.value?.trim() || '', 'phone')}
							/>

							<YesNoRadio
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_whatsapp_title`)}
								name="whatsApp"
							/>
							{formValidationError && (
								<span className="form_error">
									{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_whatsapp_error_message`)}
								</span>
							)}
						</div>
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_button`)}
							extraClassName="confirm"
							small
						/>
					</form>
				</Modal>
				<Modal
					extraClassName="certificate_modal"
					isVisible={popupBeforeCertificateModal}
					setVisibility={setPopupBeforeCertificateModalToggle}
				>
					<div className="certificate_modal__content">
						<div
							className="certificate_modal__content__title"
							dangerouslySetInnerHTML={{ __html: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_title`) }}
						></div>
						<div className="btn_wrapper">
							<Button
								extraClassName="continue"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_continue_button`)}
								onClick={() => redirectToReview(recordId)}
							/>
							<Button
								extraClassName="not_now"
								title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_not_now_button`)}
								onClick={_handleCertificate}
							/>
						</div>
					</div>
				</Modal>
				<Modal isVisible={certificateModal} setVisibility={setCertificateModalToggle} extraClassName="certificate_pdf">
					<img className="certificate_pdf__img" src={`${certificatePdf}`} />
					<Button
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_modal_download_button`)}
						onClick={_handleDownLoadPdf}
						endIcon="downloadIcon"
						small
					/>
				</Modal>
				<Modal isVisible={pdfModal} setVisibility={PdfModalToggle} extraClassName="pdf_modal">
					<object data={pdfToDisplay + '#view=FitH'} type="application/pdf" width="100%" height="100%">
						<UnsupportedFile linkToDisplay={pdfToDisplay} />
					</object>
				</Modal>
				<Tooltip ref={toolTipRef}>{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_tooltip_note`)}</Tooltip>
			</Container>
		</div>
	);
};

export const YesNoRadio = ({ title, name }) => {
	const getTranslation = useTranslation();

	return (
		<div className="YesNoRadio">
			<div className="YesNoRadio__title">{title}</div>
			<div className="YesNoRadio__radios_wrapper">
				<div className="radio">
					<input type="radio" id="no" name={name} value="0" defaultChecked />
					<label htmlFor="no">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_whatsapp_no_answer`)}</label>
				</div>
				<div className="radio">
					<input type="radio" id="yes" name={name} value="1" />
					<label htmlFor="yes">
						{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_confirm_user_details_modal_whatsapp_yes_answer`)}
					</label>
				</div>
			</div>
		</div>
	);
};

export const StudentSchedule = ({ meetings, extraClassName, courseType }) => {
	const className = classNames('StudentSchedule', { [extraClassName]: extraClassName });
	const getTranslation = useTranslation();

	const zoomLink = meetings && meetings.find(meet => meet?.meetingRemark);
	const zoomPassCode = '400001';

	//Split the link from the meeting remark
	const meetingLink = meet => {
		if (!meet?.meetingRemark) return;

		const splitedMeetingRemark = meet?.meetingRemark?.split('http');

		//Return just the link without the extra text
		//The else option is for situations that the meeting remark is just link without text.
		return splitedMeetingRemark && splitedMeetingRemark?.length > 1 ? 'http' + splitedMeetingRemark[1] : meet?.meetingRemark;
	};

	return (
		<>
			<div className={className}>
				<div className="StudentSchedule__headers__number header">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_session_field`)}
				</div>
				<div className="StudentSchedule__headers__date header">
					{' '}
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_date_field`)}
				</div>
				<div className="StudentSchedule__headers__time header">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_time_field`)}
				</div>
				<div className="StudentSchedule__headers__teacher header">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_teacher_field`)}
				</div>
				<div className="StudentSchedule__headers__zoom_link header">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_link_field`)}
				</div>
				{meetings &&
					meetings.map((meet, index) => {
						const lastMeeting = index === meetings.length - 1;
						return (
							<>
								<div key={index} className={`StudentSchedule__session__number${lastMeeting ? ' last_meeting' : ''}`}>
									{index + 1}
								</div>
								<>
									<div className="StudentSchedule__session__date">{meet.date}</div>
									<div className="StudentSchedule__session__time">{meet.startTime}</div>
								</>
								<div className="StudentSchedule__session__teacher">{meet.teacherName}</div>
								{courseType !== COURSE_TYPES.IELTS && (
									<div
										className="StudentSchedule__session__zoom_link"
										// onClick={e => copyToClipboard && typeof copyToClipboard === 'function' && copyToClipboard(meet.meetingRemark, e)}
									>
										<a href={meetingLink(meet)} target="_blank">
											Click Here
										</a>
									</div>
								)}
							</>
						);
					})}
			</div>
			{courseType === COURSE_TYPES.IELTS && zoomLink && (
				<div className="StudentSchedule__zoom_link">
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_class_link_field_title`)}{' '}
					<a dangerouslySetInnerHTML={{ __html: meetingLink(zoomLink) }} href={meetingLink(zoomLink)} target="_blank"></a>
					<br />
					{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_class_link_field_text`)}{' '}
					<span className="black">{zoomPassCode}</span>
				</div>
			)}
		</>
	);
};

export const StudentScheduleMobile = ({ meetings, extraClassName }) => {
	const className = classNames('StudentScheduleMobile', { [extraClassName]: extraClassName });
	const getTranslation = useTranslation();

	return (
		<div className={className}>
			{meetings &&
				meetings.map((meet, key) => {
					return (
						<div className="StudentScheduleMobile__session" key={key}>
							<div className="StudentScheduleMobile__session__title">
								{`${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_student_schedule_session_field`)} ${key + 1}`}
							</div>
							<div className="StudentScheduleMobile__session__content">
								<div className="StudentScheduleMobile__session__content__date">{meet.date}</div>
								<div className="StudentScheduleMobile__session__content__time">
									{meet.startTime}-{meet.endTime}
								</div>
								<div className="StudentScheduleMobile__session__content__teacher">{meet.teacherName}</div>
							</div>
							<div className="StudentScheduleMobile__session__zoom_link">
								<a href={meet.meetingRemark} target="_blank">
									Click here
								</a>
							</div>
						</div>
					);
				})}
		</div>
	);
};

export default MyCourse;
